import baselineLoad from '../data/baseline-load.json';

/**
 * Floor of every numeric scaling input, keyed product → baseline profile →
 * { field: value }. Calibrated products (ai_center, apps, semantic_proxy)
 * carry reference loads; the rest carry the products' constant defaults.
 */
const data = baselineLoad as unknown as Record<string, Record<string, Record<string, number>>>;

/**
 * Returns the baseline load for a product on a baseline profile. A missing
 * profile block falls back to the product's 'default' block — per-profile
 * blocks exist only where a profile is calibrated differently.
 */
export function getBaselineLoad(
  productKey: string,
  baselineProfile: string,
): Record<string, number> | undefined {
  return data[productKey]?.[baselineProfile] ?? data[productKey]?.['default'];
}

/**
 * Excess of a value over the calibrated baseline load for a field:
 * max(0, value − baseline). Fields (or products) without a calibrated
 * baseline have baseline 0, so the excess is the full value.
 */
export function getBaselineExcess(
  productKey: string,
  baselineProfile: string,
  field: string,
  value: number,
): number {
  const baseline = getBaselineLoad(productKey, baselineProfile)?.[field];
  return Math.max(0, value - (typeof baseline === 'number' ? baseline : 0));
}

/**
 * Per-field floor map for a product+profile, read solely from baseline-load.json.
 * formData is SPARSE — an absent numeric prop means "at reference" and resolves
 * to this floor; it is also the exact value in a read-only `default` profile.
 */
export function getFloorProperties(
  productKey: string,
  baselineProfile: string,
): Record<string, number> {
  return { ...(getBaselineLoad(productKey, baselineProfile) || {}) };
}

/** Floor for a single numeric field (0 when the field has no baseline/default). */
export function getFloor(
  productKey: string,
  baselineProfile: string,
  field: string,
): number {
  const f = getFloorProperties(productKey, baselineProfile)[field];
  return typeof f === 'number' ? f : 0;
}

/**
 * Hard ceiling for a numeric field (baseline-load.json per-product 'ceilings'
 * block), or `undefined` if uncapped. HTML `max` only constrains spinner
 * arrows, so the cap is enforced here (calc + blur clamp + widget max).
 */
export function getCeiling(
  productKey: string,
  field: string,
): number | undefined {
  return data[productKey]?.['ceilings']?.[field];
}

/**
 * Effective numeric scaling props. `default` is pinned at the floor (stored
 * values ignored — read-only reference). lite/ha are editable but clamped to
 * [floor, ceiling]: each numeric field = min(max(storedFinite, floor), ceiling);
 * a missing/NaN/cleared value resolves to the floor (this is what closes the
 * NaN→max-tier hole). The ceiling is the upper mirror of the floor so a
 * stored/URL value above the cap (e.g. retentionDays 200) sizes at the cap,
 * keeping display===calc at both ends. Numeric-only — callers merge over
 * `properties` so non-numeric flags survive.
 */
export function getEffectiveScalingProperties(
  productKey: string,
  baselineProfile: string,
  currentProperties: Record<string, any> = {},
): Record<string, number> {
  const floor = getFloorProperties(productKey, baselineProfile);
  if (baselineProfile === 'default') {
    return { ...floor };
  }
  const result: Record<string, number> = { ...floor };
  for (const [k, v] of Object.entries(currentProperties)) {
    if (typeof v !== 'number' || !Number.isFinite(v)) continue;
    let val = k in floor ? Math.max(v, floor[k]) : v;
    const ceiling = getCeiling(productKey, k);
    if (ceiling !== undefined) {
      // Floor is the authoritative minimum: if a ceilings entry is ever
      // misconfigured below the floor, clamp the ceiling up to the floor so it
      // can never undercut it.
      const effectiveCeiling = k in floor ? Math.max(ceiling, floor[k]) : ceiling;
      val = Math.min(val, effectiveCeiling);
    }
    result[k] = val;
  }
  return result;
}

